// 分享播放頁（plan/vms-web-v0.md §5.8 EXP-05）：免登入。/share/<token>[?env=]；有密碼先問；到期 / 撤銷 / 次數用完顯示已失效（410）。
// 畫面上疊浮水印（伺服器給的「收件人遮罩 · 識別碼」+ 現在時間，platform#62）；檔案經 app-api 公開端點，每個請求都查撤銷 / 到期。
// 公開 metadata 不帶內部匯出原因（收件人常在組織外）。
// 對外的收件人看不到設備 ID（SPEC #vms-web-973「畫面不出現原始值」，P0-7）：片段寫「片段 1」與時間，後端有給設備名稱（device_name）才顯示名稱。
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { planeBase } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { PasswordInput } from '../../components/PasswordInput';
import { stamp } from '../../lib/timefmt';

interface ShareMeta {
  files: { n: number; name: string; device_ref: string; device_name?: string; from: string; to: string; size: number }[];
  expires_at: string;
  /** <收件人遮罩> · <識別碼>（舊連結只有識別碼） */
  watermark: string;
  created_at: string;
  access?: string;
}

export function SharePage() {
  const { t } = useTranslation();
  const { config } = useApp();
  const { token = '' } = useParams();
  const [sp] = useSearchParams();
  const api = config.planes?.[sp.get('env') ?? config.env] ?? Object.values(config.planes ?? {})[0] ?? '';
  const base = `${planeBase(api)}/v1/vms/share/${encodeURIComponent(token)}`;
  const [meta, setMeta] = useState<ShareMeta | null>(null);
  const [state, setState] = useState<'loading' | 'password' | 'wrong' | 'gone' | 'notfound' | 'ok' | 'error'>('loading');
  const [pw, setPw] = useState('');
  const [sel, setSel] = useState(0);
  const [clock, setClock] = useState(() => new Date());

  async function load(password?: string) {
    setState('loading');
    try {
      const res = await fetch(base, { headers: password ? { 'X-Kinio-Share-Password': password } : {} });
      if (res.status === 200) {
        setMeta((await res.json()) as ShareMeta);
        setState('ok');
      } else if (res.status === 401) {
        const b = (await res.json().catch(() => ({}))) as { error?: string };
        setState(b.error === 'WRONG_PASSWORD' ? 'wrong' : 'password');
      } else if (res.status === 410) setState('gone');
      else if (res.status === 404) setState('notfound');
      else setState('error');
    } catch {
      setState('error');
    }
  }
  useEffect(() => {
    void load();
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const file = meta?.files[sel];
  const src = file ? `${base}/files/${file.n}${meta?.access ? `?a=${encodeURIComponent(meta.access)}` : ''}` : '';
  return (
    <main className="share-page">
      <header className="share-head">
        <strong>{config.display_name}</strong>
        <span className="muted">{t('share.title')}</span>
      </header>
      {state === 'loading' && <p className="muted">{t('common.loading')}</p>}
      {(state === 'password' || state === 'wrong') && (
        <form
          className="card form"
          onSubmit={(e) => {
            e.preventDefault();
            void load(pw);
          }}
        >
          <p>{t('share.need_password')}</p>
          <PasswordInput autoComplete="off" value={pw} onChange={(e) => setPw(e.target.value)} aria-label={t('share.password')} />
          {state === 'wrong' && <p className="error">{t('share.wrong_password')}</p>}
          <button className="btn btn-primary">{t('share.open')}</button>
        </form>
      )}
      {state === 'gone' && <p className="notice">{t('share.gone')}</p>}
      {state === 'notfound' && <p className="notice">{t('share.not_found')}</p>}
      {state === 'error' && <p className="error">{t('share.error')}</p>}
      {state === 'ok' && meta && (
        <>
          {meta.files.length > 1 && (
            <div className="toolbar wrap">
              {meta.files.map((f, i) => (
                <button key={f.n} className={`chip ${i === sel ? 'on' : ''}`} onClick={() => setSel(i)}>
                  {f.device_name ? `${f.device_name} · ` : ''}
                  {t('share.clip_n', { n: i + 1 })}
                </button>
              ))}
            </div>
          )}
          <div className="share-video">
            <video key={src} src={src} controls playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} />
            <div className="share-watermark" aria-hidden>
              {meta.watermark} · {stamp(clock, { seconds: true })}
            </div>
          </div>
          {file && (
            <p className="muted small" data-testid="share-file">
              {file.device_name ? `${file.device_name} · ` : ''}
              {stamp(file.from, { seconds: true })} – {stamp(file.to, { seconds: true })}
            </p>
          )}
          <p className="muted small">{t('share.expires', { at: stamp(meta.expires_at) })}</p>
        </>
      )}
    </main>
  );
}
